/**
 * VoiceButton — composer mic button. Wraps a click-to-toggle
 * VoiceRecognizer; while listening, mirrors the live transcript into the
 * active text input via the parent's `onAppend` callback.
 *
 * Hides itself when the browser doesn't support speech recognition (per
 * `plan-arch-agent-ui.md` — degrade silently rather than render a
 * broken affordance).
 */

import { ComposerIconButton } from '@ordo/ui/composer-primitives'
import { Mic, MicOff } from 'lucide-react'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { useEmbedConfig } from '../hooks/use-embed-config'
import { isVoiceSupported, VoiceRecognizer } from '../lib/voice-recognition'
import { useAgentStore } from '../stores/agent-store'

export interface VoiceButtonHandle {
  stop(): void
}

interface VoiceButtonProps {
  /** Called with each delta of finalized transcript text — append to your composer. */
  onAppend: (delta: string) => void
  disabled?: boolean
}

export const VoiceButton = forwardRef<VoiceButtonHandle, VoiceButtonProps>(({ onAppend, disabled }, ref) => {
  // Read the org-wide voice toggle set in /admin/embed. The hook is
  // module-cached per hostApp; surfaces (AgentTopbar / SurfaceBody)
  // populate that cache on mount, so by the time the composer (and
  // therefore this button) renders, the value is present without a
  // round-trip.
  const hostApp = useAgentStore((s) => s.hostApp) ?? ''
  const serverConfig = useEmbedConfig(hostApp)
  const [supported, setSupported] = useState(false)
  const [listening, setListening] = useState(false)
  const recognizerRef = useRef<VoiceRecognizer | null>(null)
  const lastFinalRef = useRef('')

  useEffect(() => {
    setSupported(isVoiceSupported())
  }, [])

  const stop = useCallback(() => {
    recognizerRef.current?.stop()
    recognizerRef.current = null
    setListening(false)
  }, [])

  useImperativeHandle(ref, () => ({ stop }), [stop])

  const start = useCallback(() => {
    if (recognizerRef.current) {
      return
    }
    const r = new VoiceRecognizer()
    recognizerRef.current = r
    lastFinalRef.current = ''
    setListening(true)
    r.start({
      onInterim: (text) => {
        // Append only the delta beyond what we've already committed.
        if (!text || text === lastFinalRef.current) {
          return
        }
        if (text.startsWith(lastFinalRef.current)) {
          const delta = text.slice(lastFinalRef.current.length)
          if (delta) {
            onAppend(delta)
            lastFinalRef.current = text
          }
        }
      },
      onFinal: (text) => {
        if (text && text !== lastFinalRef.current && text.startsWith(lastFinalRef.current)) {
          const delta = text.slice(lastFinalRef.current.length)
          if (delta) {
            onAppend(delta)
          }
        }
        recognizerRef.current = null
        setListening(false)
      },
      onError: () => {
        recognizerRef.current = null
        setListening(false)
      },
    })
  }, [onAppend])

  useEffect(() => () => recognizerRef.current?.stop(), [])

  if (!supported) {
    return null
  }
  // Admin disabled voice input org-wide.
  if (serverConfig?.voiceInput === false) {
    return null
  }

  return (
    <ComposerIconButton
      density="compact"
      onClick={() => (listening ? stop() : start())}
      disabled={disabled}
      variant={listening ? 'destructive' : 'ghost'}
      aria-label={listening ? 'Stop voice input' : 'Start voice input'}>
      {listening ? <MicOff /> : <Mic />}
    </ComposerIconButton>
  )
})
VoiceButton.displayName = 'VoiceButton'
